import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Card, ErrorState } from './components';

/* --------------------------------------------------------------------------
   Route-level error boundary.

   No screen may take the whole router with it: a malformed payload that makes
   one screen throw used to unmount every route, including the ones whose data
   is fine. Each route's element is wrapped in one of these where `guarded()`
   is applied in `App.tsx`, so the blast radius of a throw is the route that
   threw — navigation and every other route keep working.

   The fallback wears the existing face (`ErrorState` with retry), not a new
   design. The `key` on each use is load-bearing: every route's element sits
   at the same position in the tree, so without it a boundary that caught on
   one route would stay caught after navigating to another.
   ----------------------------------------------------------------------- */

interface RouteBoundaryProps {
  children: ReactNode;
}

interface RouteBoundaryState {
  error: Error | null;
}

export class RouteBoundary extends Component<RouteBoundaryProps, RouteBoundaryState> {
  state: RouteBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // There is no reporting pipeline; the console keeps the trace out of the
    // UI without swallowing it.
    console.error('[route-boundary]', error, info.componentStack);
  }

  private retry(): void {
    this.setState({ error: null });
  }

  render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <div className="screen">
          <Card><ErrorState error={this.state.error} onRetry={() => this.retry()} /></Card>
        </div>
      );
    }
    return this.props.children;
  }
}
